// Role catalog. Roles bundle a default set of accessible modules + a default
// bottom-nav layout — they're the "templates" an admin picks when onboarding
// a user. After assignment, the user (or admin) can override their own config.

import type { ModuleKey } from '@erp/data-access/interfaces';

export type RoleKey = 'admin' | 'manager' | 'cashier' | 'salesman' | 'storekeeper' | 'accountant';

export interface RoleDefinition {
  key: RoleKey;
  /** Translation key under `mobile.roles.<key>.{title,description}`. */
  i18nKey: string;
  /** Modules visible to this role. `'all'` = every enabled module. */
  allowedModules: ModuleKey[] | 'all';
  /** Default bottom nav screen ids for this role (applied if user has no override). */
  defaultBottomNav: string[];
  /** Whether the role can edit the App Builder for the whole tenant. */
  canEditTenantConfig: boolean;
}

export const ROLE_CATALOG: readonly RoleDefinition[] = [
  {
    key: 'admin',
    i18nKey: 'admin',
    allowedModules: 'all',
    defaultBottomNav: ['dashboard', 'sales.invoices.list', 'clients.list', 'caisse.pos', 'settings.hub'],
    canEditTenantConfig: true,
  },
  {
    key: 'manager',
    i18nKey: 'manager',
    allowedModules: ['sales', 'purchases', 'stock', 'payments', 'treasury', 'checks', 'withholding', 'caisse', 'storefront'],
    defaultBottomNav: ['dashboard', 'sales.invoices.list', 'clients.list', 'stock.movements', 'settings.hub'],
    canEditTenantConfig: false,
  },
  {
    key: 'cashier',
    i18nKey: 'cashier',
    allowedModules: ['sales', 'caisse', 'stock', 'payments'],
    defaultBottomNav: ['caisse.pos', 'caisse.dashboard', 'articles.scan', 'sales.invoices.list', 'profile'],
    canEditTenantConfig: false,
  },
  {
    key: 'salesman',
    i18nKey: 'salesman',
    allowedModules: ['sales', 'caisse'],
    defaultBottomNav: ['dashboard', 'clients.list', 'sales.quotes.form', 'sales.invoices.list', 'profile'],
    canEditTenantConfig: false,
  },
  {
    key: 'storekeeper',
    i18nKey: 'storekeeper',
    allowedModules: ['sales', 'purchases', 'stock'],
    defaultBottomNav: ['dashboard', 'stock.movements', 'stock.inventory', 'purchases.receptions.list', 'profile'],
    canEditTenantConfig: false,
  },
  {
    key: 'accountant',
    i18nKey: 'accountant',
    allowedModules: ['sales', 'purchases', 'payments', 'treasury', 'checks', 'withholding'],
    defaultBottomNav: ['dashboard', 'sales.invoices.list', 'payments.list', 'checks.list', 'withholding.sales.list'],
    canEditTenantConfig: false,
  },
];

export function getRole(key: RoleKey): RoleDefinition | undefined {
  return ROLE_CATALOG.find((r) => r.key === key);
}
